/**
 * « Corriger automatiquement » : un brouillon qui ne se joue pas à cause
 * d'erreurs MÉCANIQUES (« draw » sur un texte, `dash: "true"`…) est corrigé
 * en un toucher, sans régénérer. La création par l'IA le fait d'office depuis
 * le 28/10 : ce bouton corrige un brouillon plus ancien.
 */

import { useMemo } from 'react';
import { Wand2 } from 'lucide-react';
import { autoFixJraf } from '@jeraisonne/animation';
import { checkedDraft, useSaveAnimationDraft, type AnimationRow } from '@/lib/animations/api';

export function AnimationAutoFix({
  row,
  toolIds,
}: {
  row: Pick<AnimationRow, 'id' | 'draft'>;
  toolIds?: readonly string[];
}) {
  const { fixes } = useMemo(() => autoFixJraf(row.draft), [row.draft]);
  const save = useSaveAnimationDraft();
  if (fixes.length === 0) return null;
  const n = fixes.length;
  return (
    <div className="border-primary-200 bg-primary-50 rounded-xl border p-3">
      <p className="text-ink-700 text-[13px]">
        {n > 1
          ? `${n} erreurs simples se corrigent d’office`
          : 'Une erreur simple se corrige d’office'}{' '}
        : {fixes.map((f) => f.message).join(' ; ')}.
      </p>
      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate({ id: row.id, ...checkedDraft(row.draft, toolIds) })}
        className="bg-primary-500 hover:bg-primary-600 mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-60"
      >
        <Wand2 className="h-4 w-4" aria-hidden />
        {save.isPending ? 'Enregistrement…' : 'Corriger automatiquement'}
      </button>
      {save.error && (
        <p role="alert" className="text-error-600 mt-2 text-[13px]">
          {save.error.message}
        </p>
      )}
    </div>
  );
}
